import { useFlags } from "@aichat/aichat-lib";
import { FlagsmithTraits } from "@aichat/types/index";
import {
  ButtonPayloadEnum,
  ChatResponse,
  MessageAttachment,
  MessageContent,
  MessageEmit,
} from "@aichat/types/socket/widget.types";
import { HandleSendComponentParams } from "@aichat/types/widget";
import React, { useEffect, useRef, useState } from "react";

type UseWebWidget = {
  chat: ChatResponse | null;
  messages: MessageContent[];
  // eslint-disable-next-line no-unused-vars
  onSend: (args: MessageEmit["message"]) => void;
  isStreaming: boolean;
};
const useWebWidget = ({ messages, onSend, isStreaming }: UseWebWidget) => {
  const [inputText, setInputText] = useState("");
  const [attachments, setAttachments] = useState<MessageAttachment[]>([]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [interruptAvatar, setInterruptAvatar] = useState(false);
  const { digital_human } = useFlags([], [FlagsmithTraits.DIGITAL_HUMAN]);

  const chatListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (digital_human && !interruptAvatar) {
      const timeout = setTimeout(() => {
        if (chatListRef.current) {
          chatListRef.current.scrollBy({
            top: chatListRef.current.clientHeight,
            behavior: "smooth",
          });
        }
      }, 500);
      const interval = setInterval(
        () => {
          if (chatListRef.current) {
            if (
              chatListRef.current.scrollTop + chatListRef.current.clientHeight <
              chatListRef.current.scrollHeight
            ) {
              chatListRef.current.scrollBy({
                top: chatListRef.current.clientHeight,
                behavior: "smooth",
              });
            } else {
              clearInterval(interval);
            }
          }
        },
        ((chatListRef.current?.clientHeight || 0) / 16) * 1200,
      );
      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    } else {
      const timeout = setTimeout(() => {
        if (chatListRef.current) {
          chatListRef.current.scrollTo({
            top: chatListRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 500);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [messages.length, digital_human, interruptAvatar]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [isStreaming]);

  const handleSend = async (args?: {
    text?: string;
    callback?: () => void;
  }) => {
    if ((args?.text?.length ?? 0) <= 0 && inputText.length <= 0) return;

    if (args?.text) onSend({ text: args?.text, attachments });
    else if (attachments.length > 0)
      onSend({ text: args?.text ?? inputText ?? "", attachments });
    else if (inputText && !args?.text) onSend({ text: inputText, attachments });
    setInputText("");
    setInterruptAvatar(false);
    setAttachments([]);
    args?.callback?.();
  };

  const handleSendComponent = ({ type, value }: HandleSendComponentParams) => {
    switch (type) {
      case ButtonPayloadEnum.REPLY:
      case ButtonPayloadEnum.STARTER:
        onSend({ text: value, attachments: [] });
        break;
      default:
        break;
    }
  };

  const handleSendForm = (data: MessageEmit["message"]["form_response"]) => {
    onSend({
      text: "",
      attachments: [],
      form_response: data,
    });
  };

  const onKeyDown = (args: {
    e: React.KeyboardEvent<HTMLTextAreaElement>;
    text?: string;
    callback?: () => void;
  }) => {
    const { e, text, callback } = args;
    if (!e.shiftKey && e.key === "Enter") {
      e.preventDefault();
      handleSend({ text, callback });
      return;
    }
  };

  return {
    chatListRef,
    inputText,
    onKeyDown,
    setInputText,
    handleSend,
    inputRef,
    handleSendComponent,
    interruptAvatar,
    setInterruptAvatar,
    attachments,
    setAttachments,
    handleSendForm,
  };
};

export default useWebWidget;
